import { z } from 'zod';
import { priceConditionSourceSchema } from './price-group.js';
import { EXPORT_FORMATS } from '../constants/app.js';
import { PACKAGINGS_MAX_COUNT, PACKAGING_UNIT_CODES, PRICE_TIERS_MAX_COUNT, PRICE_UPDATE_MAX_PRODUCTS, PRODUCT_SORT_FIELDS, PRODUCT_TAGS_MAX_COUNT, PRODUCT_TAG_MAX_LENGTH, } from '../constants/products.js';
import { commaSeparated, countryCodeSchema, currencyCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, nonNegativeQuantitySchema, percentSchema, positiveQuantitySchema, } from './common.js';
import { customFieldFiltersQuerySchema, customFieldValuesSchema } from './custom-field.js';
import { pickingStrategySchema, productStatusSchema, productTypeSchema, taxRateTypeSchema, } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
import { productCategoryAssignmentSchema, productCategoryAssignmentsWriteSchema, productCategorySchema, } from './product-category.js';
import { productImageSchema } from './product-image.js';
import { productUsageRolesSchema } from './plm-material.js';
const optionalText = (max) => z.string().trim().max(max).nullish();
export const unitCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_-]{0,39}$/u);
export const productBaseUnitSchema = z.object({
    code: unitCodeSchema,
    translations: z.record(z.object({ name: z.string() })),
});
export const productBaseUnitsSchema = z.array(productBaseUnitSchema);
export const packagingUnitCodeSchema = z.enum(PACKAGING_UNIT_CODES);
export const productTagSchema = z.string().trim().min(1).max(PRODUCT_TAG_MAX_LENGTH);
export const productTagsSchema = z.array(productTagSchema).max(PRODUCT_TAGS_MAX_COUNT);
/** GTIN-8/12/13/14 inklusive Prüfziffer nach GS1. */
export const eanSchema = z
    .string()
    .trim()
    .regex(/^\d{8}$|^\d{12,14}$/, 'Eine GTIN besteht aus 8, 12, 13 oder 14 Ziffern.')
    .superRefine((value, ctx) => {
    if (!isValidGtin(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Die Prüfziffer der GTIN ist falsch.',
        });
    }
});
export function isValidGtin(value) {
    if (!/^\d{8}$|^\d{12,14}$/.test(value)) {
        return false;
    }
    const digits = [...value].map(Number);
    const checkDigit = digits.pop() ?? 0;
    const sum = digits
        .reverse()
        .reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 3 : 1), 0);
    return (10 - (sum % 10)) % 10 === checkDigit;
}
/** Zolltarifnummer: 8 Stellen für die Ausfuhr, 11 für Intrastat-Meldungen. */
export const commodityCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{8}$|^\d{10,11}$/, 'Die Warennummer besteht aus 8, 10 oder 11 Ziffern.');
// ---------------------------------------------------------------------------
// Staffelpreise
// ---------------------------------------------------------------------------
export const productPriceTierSchema = z.object({
    id: idSchema,
    productId: idSchema,
    customerId: idSchema.nullish(),
    sourcePriceList: z.string().nullish(),
    priceGroupId: idSchema.nullish(),
    priceGroupName: z.string().nullish(),
    priceGroup: z.string().nullable(),
    currency: z.string(),
    unitCode: z.string(),
    minQuantity: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    validFrom: isoDateSchema.nullable(),
    validTo: isoDateSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const productPriceTierWriteSchema = z
    .object({
    id: idSchema.optional(),
    priceGroupId: idSchema.nullish(),
    priceGroup: optionalText(50),
    currency: currencyCodeSchema.default('EUR'),
    unitCode: z.string().trim().min(1).max(10).optional(),
    minQuantity: nonNegativeQuantitySchema.default('0'),
    unitPrice: moneySchema,
    discountPct: percentSchema.default('0'),
    validFrom: isoDateSchema.nullish(),
    validTo: isoDateSchema.nullish(),
})
    .superRefine((value, ctx) => {
    if (typeof value.validFrom === 'string' &&
        typeof value.validTo === 'string' &&
        value.validTo < value.validFrom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['validTo'],
            message: 'Das Gültigkeitsende darf nicht vor dem Beginn liegen.',
        });
    }
});
export const productPriceTiersWriteSchema = z
    .array(productPriceTierWriteSchema)
    .max(PRICE_TIERS_MAX_COUNT);
// ---------------------------------------------------------------------------
// Gebinde
// ---------------------------------------------------------------------------
export const productPackagingSchema = z.object({
    packagingTypeId: idSchema.nullish(),
    isSummaryDefault: z.boolean().optional(),
    isWeightDefault: z.boolean().optional(),
    id: idSchema,
    productId: idSchema,
    code: z.string(),
    name: z.string(),
    unitCode: z.string(),
    factor: z.string(),
    ean: z.string().nullable(),
    isSalesDefault: z.boolean(),
    isPurchaseDefault: z.boolean(),
    weightGrossKg: z.string().nullable(),
    lengthMm: z.number().int().nullable(),
    widthMm: z.number().int().nullable(),
    heightMm: z.number().int().nullable(),
    minOrderQuantity: z.string().nullable(),
    orderStepQuantity: z.string().nullable(),
    customFields: customFieldValuesSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const productPackagingWriteSchema = z.object({
    packagingTypeId: idSchema.nullish(),
    isSummaryDefault: z.boolean().optional(),
    isWeightDefault: z.boolean().optional(),
    id: idSchema.optional(),
    code: z.string().trim().min(1).max(30),
    name: z.string().trim().min(1).max(100),
    unitCode: z.string().trim().min(1).max(10).default('BX'),
    factor: positiveQuantitySchema,
    ean: eanSchema.nullish(),
    isSalesDefault: z.boolean().default(false),
    isPurchaseDefault: z.boolean().default(false),
    weightGrossKg: decimalWeight().nullish(),
    lengthMm: z.number().int().min(0).max(100_000).nullish(),
    widthMm: z.number().int().min(0).max(100_000).nullish(),
    heightMm: z.number().int().min(0).max(100_000).nullish(),
    minOrderQuantity: nonNegativeQuantitySchema.nullish(),
    orderStepQuantity: nonNegativeQuantitySchema.nullish(),
    customFields: customFieldValuesSchema.default({}),
});
export const productPackagingsWriteSchema = z
    .array(productPackagingWriteSchema)
    .max(PACKAGINGS_MAX_COUNT);
function decimalWeight() {
    return z
        .string()
        .trim()
        .regex(/^\d{1,12}(\.\d{1,6})?$/, 'Erwartet wird ein Gewicht mit maximal 6 Nachkommastellen.');
}
// ---------------------------------------------------------------------------
// Artikel
// ---------------------------------------------------------------------------
export const productSchema = z.object({
    hasMigrationSource: z.boolean().optional(),
    migrationReviewRequired: z.boolean().optional(),
    id: idSchema,
    articleNumber: z.string(),
    type: productTypeSchema.nullable(),
    usageRoles: productUsageRolesSchema,
    status: productStatusSchema,
    name: z.string(),
    nameEn: z.string().nullable(),
    nameTr: z.string().nullish(),
    nameFr: z.string().nullish(),
    description: z.string().nullable(),
    descriptionEn: z.string().nullable(),
    descriptionTr: z.string().nullish(),
    descriptionFr: z.string().nullish(),
    ean: z.string().nullable(),
    manufacturerNumber: z.string().nullable(),
    productGroup: z.string().nullable(),
    baseUnit: z.string(),
    taxRateType: taxRateTypeSchema,
    taxRatePct: z.string(),
    currency: z.string(),
    listPrice: z.string().nullable(),
    purchasePrice: z.string(),
    minMarginPct: z.string(),
    weightNetKg: z.string().nullable(),
    weightGrossKg: z.string().nullable(),
    commodityCode: z.string().nullable(),
    countryOfOrigin: z.string().nullable(),
    stockQuantity: z.string(),
    reservedQuantity: z.string(),
    availableQuantity: z.string(),
    minStock: z.string(),
    reorderPoint: z.string(),
    targetStock: z.string(),
    leadTimeDays: z.number().int(),
    isBelowMinStock: z.boolean(),
    isBatchManaged: z.boolean(),
    isShelfLifeManaged: z.boolean(),
    shelfLifeDays: z.number().int().nullable(),
    minRemainingShelfLifeDays: z.number().int().nullable(),
    pickingStrategy: pickingStrategySchema,
    tags: z.array(z.string()),
    customFields: customFieldValuesSchema,
    categories: z.array(productCategoryAssignmentSchema.extend({ category: productCategorySchema })),
    primaryCategory: productCategorySchema.nullable(),
    primaryImage: productImageSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const productDetailSchema = productSchema.extend({
    priceTiers: z.array(productPriceTierSchema),
    hasCalculatedPrices: z.boolean().optional(),
    packagings: z.array(productPackagingSchema),
    batchSummary: z.object({
        total: z.number().int(),
        blocked: z.number().int(),
        expired: z.number().int(),
        critical: z.number().int(),
        warning: z.number().int(),
    }),
});
export const productListItemSchema = productSchema
    .pick({
    id: true,
    migrationReviewRequired: true,
    hasMigrationSource: true,
    articleNumber: true,
    type: true,
    status: true,
    name: true,
    ean: true,
    productGroup: true,
    baseUnit: true,
    currency: true,
    listPrice: true,
    purchasePrice: true,
    taxRatePct: true,
    stockQuantity: true,
    reservedQuantity: true,
    availableQuantity: true,
    minStock: true,
    isBelowMinStock: true,
    isBatchManaged: true,
    isShelfLifeManaged: true,
    tags: true,
    customFields: true,
    primaryCategory: true,
    primaryImage: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    /** Anzahl offener Chargen und schärfste Ampelstufe für die Listenanzeige. */
    batchCount: z.number().int(),
    expiringBatchCount: z.number().int(),
});
export const productWriteSchema = z.object({
    /** Leer lassen: Die Nummer wird aus dem Nummernkreis PRODUCT vergeben. */
    articleNumber: z.string().trim().min(1).max(30).nullish(),
    type: productTypeSchema.default('STOCK'),
    usageRoles: productUsageRolesSchema.default([]),
    status: productStatusSchema.default('ACTIVE'),
    name: z.string().trim().min(1).max(200),
    nameEn: optionalText(200),
    nameTr: optionalText(200),
    nameFr: optionalText(200),
    description: optionalText(4000),
    descriptionEn: optionalText(4000),
    descriptionTr: optionalText(4000),
    descriptionFr: optionalText(4000),
    ean: eanSchema.nullish(),
    manufacturerNumber: optionalText(50),
    productGroup: optionalText(80),
    baseUnit: unitCodeSchema.default('PCE'),
    taxRateType: taxRateTypeSchema.default('STANDARD'),
    taxRatePct: percentSchema.default('19'),
    currency: currencyCodeSchema.default('EUR'),
    listPrice: moneySchema.default('0'),
    purchasePrice: moneySchema.default('0'),
    minMarginPct: percentSchema.default('0'),
    weightNetKg: decimalWeight().nullish(),
    weightGrossKg: decimalWeight().nullish(),
    commodityCode: commodityCodeSchema.nullish(),
    countryOfOrigin: countryCodeSchema.nullish(),
    minStock: nonNegativeQuantitySchema.default('0'),
    reorderPoint: nonNegativeQuantitySchema.default('0'),
    targetStock: nonNegativeQuantitySchema.default('0'),
    leadTimeDays: z.number().int().min(0).max(3650).default(0),
    isBatchManaged: z.boolean().default(false),
    isShelfLifeManaged: z.boolean().default(false),
    shelfLifeDays: z.number().int().min(1).max(36_500).nullish(),
    minRemainingShelfLifeDays: z.number().int().min(0).max(3650).nullish(),
    pickingStrategy: pickingStrategySchema.default('FEFO'),
    tags: productTagsSchema.default([]),
    customFields: customFieldValuesSchema.default({}),
    priceTiers: productPriceTiersWriteSchema.optional(),
    packagings: productPackagingsWriteSchema.optional(),
    categoryAssignments: productCategoryAssignmentsWriteSchema.optional(),
});
/** MHD-Führung setzt Chargenführung voraus, weil das Datum an der Charge hängt. */
export function hasConsistentBatchSettings(input) {
    return input.isShelfLifeManaged !== true || input.isBatchManaged === true;
}
function refineProduct(value, ctx, partial = false) {
    if ((!partial || value.isBatchManaged !== undefined) && !hasConsistentBatchSettings(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['isShelfLifeManaged'],
            message: 'Die MHD-Führung erfordert einen chargengeführten Artikel.',
        });
    }
    value.priceTiers?.forEach((tier, index) => {
        if (value.priceTiers
            ?.slice(0, index)
            .some((other) => (other.priceGroupId ?? other.priceGroup ?? '') ===
            (tier.priceGroupId ?? tier.priceGroup ?? '') &&
            other.currency === tier.currency &&
            Number(other.minQuantity) === Number(tier.minQuantity) &&
            (other.validFrom ?? '0000') <= (tier.validTo ?? '9999') &&
            (tier.validFrom ?? '0000') <= (other.validTo ?? '9999'))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['priceTiers', index, 'minQuantity'],
                message: 'priceGroups.overlap',
            });
        }
    });
    const packagingCodes = new Set();
    value.packagings?.forEach((packaging, index) => {
        const key = packaging.code.toUpperCase();
        if (packagingCodes.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['packagings', index, 'code'],
                message: 'Der Gebindeschlüssel ist bereits vergeben.',
            });
        }
        packagingCodes.add(key);
    });
}
export const productCreateSchema = productWriteSchema
    .extend({ duplicateSourceId: idSchema.optional() })
    .superRefine((value, ctx) => {
    refineProduct(value, ctx);
    if (value.duplicateSourceId !== undefined && !value.articleNumber?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['articleNumber'],
            message: 'duplication.numberRequired',
        });
    }
});
export const productUpdateSchema = productWriteSchema
    .extend({
    type: productTypeSchema.nullable(),
    listPrice: moneySchema.nullable(),
})
    .partial()
    .superRefine((value, ctx) => refineProduct(value, ctx, true));
// ---------------------------------------------------------------------------
// Listenabfrage, Export und Massenpflege
// ---------------------------------------------------------------------------
export const productSortFieldSchema = z.enum(PRODUCT_SORT_FIELDS);
const booleanFlagSchema = z.preprocess((value) => {
    if (value === 'true') {
        return true;
    }
    if (value === 'false') {
        return false;
    }
    return value;
}, z.boolean().optional());
export const productListQuerySchema = paginationQuerySchema
    .omit({ sort: true })
    .extend({
    sort: z
        .string()
        .regex(/^[a-zA-Z]+:(asc|desc)$/)
        .optional(),
    status: commaSeparated(productStatusSchema),
    type: commaSeparated(productTypeSchema),
    productGroup: commaSeparated(z.string().trim().min(1).max(80)),
    tags: commaSeparated(productTagSchema),
    isBatchManaged: booleanFlagSchema,
    belowMinStock: booleanFlagSchema,
    expiringWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
    activeOnly: booleanFlagSchema,
    customFieldFilters: customFieldFiltersQuerySchema,
})
    .transform((value) => {
    const [field, order] = (value.sort ?? 'articleNumber:asc').split(':');
    const parsedField = productSortFieldSchema.safeParse(field);
    const parsedOrder = sortOrderSchema.safeParse(order);
    return {
        ...value,
        sortField: parsedField.success ? parsedField.data : 'articleNumber',
        sortOrder: parsedOrder.success ? parsedOrder.data : 'asc',
    };
});
export const productExportQuerySchema = productListQuerySchema;
export const productExportFormatSchema = z.enum(EXPORT_FORMATS);
export const productListResponseSchema = z.object({
    data: z.array(productListItemSchema),
    meta: paginationMetaSchema,
    facets: z.object({
        productGroups: z.array(z.object({ value: z.string(), count: z.number().int() })),
        belowMinStock: z.number().int(),
        expiringSoon: z.number().int(),
    }),
});
export const priceAdjustmentModeSchema = z.enum(['PERCENT', 'ABSOLUTE', 'SET']);
export const priceAdjustmentTargetSchema = z.enum(['LIST_PRICE', 'PURCHASE_PRICE']);
export const productBulkPriceUpdateSchema = z
    .object({
    productIds: z.array(idSchema).min(1).max(PRICE_UPDATE_MAX_PRODUCTS),
    target: priceAdjustmentTargetSchema.default('LIST_PRICE'),
    mode: priceAdjustmentModeSchema,
    /** Prozentwert (auch negativ), Absolutbetrag oder Festpreis, je nach Modus. */
    value: decimalStringWithSign(),
    roundTo: z.number().int().min(0).max(4).default(2),
    reason: z.string().trim().min(1).max(500),
})
    .superRefine((value, ctx) => {
    if (value.mode === 'SET' && Number(value.value) < 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'Ein Festpreis darf nicht negativ sein.',
        });
    }
    if (value.mode === 'PERCENT' && Number(value.value) <= -100) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'Eine Senkung um 100 % oder mehr ist nicht zulässig.',
        });
    }
});
function decimalStringWithSign() {
    return z
        .string()
        .trim()
        .regex(/^-?\d{1,14}(\.\d{1,4})?$/, 'Erwartet wird eine Dezimalzahl mit maximal 4 Nachkommastellen.');
}
export const productBulkPriceUpdateResultSchema = z.object({
    updated: z.number().int(),
    skipped: z.array(z.object({ productId: idSchema, reason: z.string() })),
});
export const productPriceQuerySchema = z.object({
    quantity: positiveQuantitySchema.default('1'),
    customerId: idSchema.optional(),
    documentId: idSchema.optional(),
    priceGroupId: z.preprocess((value) => (value === '' ? null : value), idSchema.nullish()),
    packagingId: idSchema.optional(),
    priceGroup: z.string().trim().min(1).max(50).optional(),
    currency: currencyCodeSchema.optional(),
    referenceDate: isoDateSchema.optional(),
});
export const productPriceResponseSchema = z.object({
    productId: idSchema,
    quantity: z.string(),
    quantityBase: z.string(),
    packagingFactor: z.string(),
    packagingId: idSchema.nullable(),
    currency: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    effectivePrice: z.string(),
    lineTotal: z.string(),
    source: z.enum(['LIST', 'TIER']),
    conditionSource: z.union([priceConditionSourceSchema, z.literal('CUSTOMER')]).optional(),
    validFrom: isoDateSchema.nullish(),
    validTo: isoDateSchema.nullish(),
    tierId: idSchema.nullable(),
    minQuantity: z.string().nullable(),
});
//# sourceMappingURL=product.js.map